import type { StorefrontStatus } from './types';

/**
 * Go-live readiness derivation — a pure view over a merchant's storefront
 * standup status. Mirrors the SERVER-authoritative gate in osprey-api
 * (provisioning.service `goLive`): a tenant is ready to flip demo→live when it is
 * created, has an API key issued, and both infra legs are provisioned. `deploy.live`
 * is NOT part of the gate (the demo is already deployed; go-live is a redeploy) —
 * a live tenant is simply the terminal state.
 */
export type ReadinessState = 'live' | 'ready' | 'in-progress' | 'not-started' | 'unknown';

export interface Readiness {
  state: ReadinessState;
  /** Human-readable prerequisites not yet satisfied (empty when ready/live). */
  missing: string[];
}

const GATE: Array<{ label: string; ok: (s: StorefrontStatus) => boolean }> = [
  { label: 'Tenant created', ok: (s) => s.tenant.created },
  { label: 'API key issued', ok: (s) => s.tenant.keyIssued },
  { label: 'Repo provisioned', ok: (s) => s.infra.repoProvisioned },
  { label: 'Site provisioned', ok: (s) => s.infra.siteProvisioned },
];

export function getReadiness(status: StorefrontStatus | undefined): Readiness {
  if (!status) return { state: 'unknown', missing: [] };
  if (status.deploy.live) return { state: 'live', missing: [] };

  const missing = GATE.filter((g) => !g.ok(status)).map((g) => g.label);
  if (missing.length === 0) return { state: 'ready', missing };

  const anyProgress =
    status.tenant.created ||
    status.tenant.onboarded ||
    status.tenant.keyIssued ||
    status.infra.repoProvisioned ||
    status.infra.siteProvisioned;
  return { state: anyProgress ? 'in-progress' : 'not-started', missing };
}

export const READINESS_LABELS: Record<ReadinessState, string> = {
  live: 'Live',
  ready: 'Ready to go live',
  'in-progress': 'In progress',
  'not-started': 'Not started',
  unknown: 'Unknown',
};

/** Order used for the summary strip + sorting (most-actionable first). */
export const READINESS_ORDER: ReadinessState[] = ['ready', 'in-progress', 'not-started', 'live', 'unknown'];
